// useVersionCheck — detects when a new build has been deployed and either
// prompts the user to reload (visible tab) or silently reloads (hidden 5+ min).
//
// Relies on /version.json being emitted at build time (see vite.config.js) and
// served with Cache-Control: no-cache by Caddy so polls always see the latest SHA.
// Complements src/stale-chunk-handler.ts (reactive reload on chunk 404) with a
// proactive deploy-detection mechanism.

import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useVersionState } from "./useVersionState";

const IDLE_RELOAD_MS = 5 * 60 * 1000; // 5 min hidden -> silent reload
const TOAST_ID = "version-update";

export function useVersionCheck() {
  const { isUpdateAvailable } = useVersionState();
  const toastShownRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isUpdateAvailable || toastShownRef.current) return;

    toastShownRef.current = true;

    console.info("[version-check] new build available");
    showUpdateToast();

    // If the tab is already hidden when we detect, start idle timer now.
    if (document.hidden) startIdleReloadTimer();

    function showUpdateToast() {
      toast(
        (t) => (
          <span className="flex items-center gap-2">
            <span>New version available.</span>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                window.location.reload();
              }}
              className="px-2 py-1 rounded bg-white/20 hover:bg-white/30 text-white font-medium"
            >
              Reload
            </button>
          </span>
        ),
        {
          id: TOAST_ID,
          duration: Infinity,
          position: "bottom-right",
        }
      );
    }

    function startIdleReloadTimer() {
      if (idleTimerRef.current) return;
      idleTimerRef.current = setTimeout(() => {
        if (document.hidden) window.location.reload();
      }, IDLE_RELOAD_MS);
    }

    function clearIdleReloadTimer() {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    }

    function onVisibility() {
      if (document.hidden) {
        startIdleReloadTimer();
      } else {
        clearIdleReloadTimer();
      }
    }

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearIdleReloadTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isUpdateAvailable]);
}
